// /admin/ops/findings — the findings inbox. Highest-traffic page.
//
// Filters mirror the API: task_type, severity, only_open, code, and the
// broker dims (SM-725: root, subtree, broker, account, currency,
// operation type, lifecycle state). URL query string is the single
// source of truth so triage views are shareable.
//
// Cursor-paginated; "Load more" appends to the visible list rather
// than replacing it, so an operator scrolling through the day's
// findings doesn't lose their place.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardBody } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { fmtRelative } from "@/lib/format";
import {
  bulkResolveFindings,
  fetchFindings,
  opsKeys,
} from "@/lib/ops-queries";
import {
  BROKER_LIFECYCLE_STATES,
  parseTaskType,
  TASK_TYPE_OPTIONS,
  type Finding,
  type Severity,
  type TaskType,
} from "@/lib/ops-types";
import { severityColor, severityLabel } from "@/lib/ops-types";

interface Search {
  task_type?: TaskType;
  severity?: string;
  only_open?: string;
  // run_id scopes the inbox to one run's findings. Set when arriving
  // from the runs page's "view related findings" cross-link.
  run_id?: string;
  code?: string;
  // SM-725 broker dims.
  root_broker_id?: string;
  subtree_broker_id?: string;
  broker_id?: string;
  broker_account_id?: string;
  currency?: string;
  operation_type?: string;
  lifecycle_state?: string;
}

const CURRENCIES = ["GOLD", "RED", "GREEN"];

/** A free-text id filter: trimmed, id-shaped, else dropped. */
function idParam(v: unknown): string | undefined {
  return typeof v === "string" && /^[A-Za-z0-9_.:\-]{1,128}$/.test(v.trim()) ? v.trim() : undefined;
}

export const Route = createFileRoute("/_app/ops/findings/")({
  validateSearch: (s: Record<string, unknown>): Search => ({
    task_type: parseTaskType(s.task_type),
    severity: typeof s.severity === "string" ? s.severity : undefined,
    only_open: typeof s.only_open === "string" ? s.only_open : "true",
    run_id:
      typeof s.run_id === "string" && /^\d+$/.test(s.run_id)
        ? s.run_id
        : undefined,
    code: idParam(s.code),
    root_broker_id: idParam(s.root_broker_id),
    subtree_broker_id: idParam(s.subtree_broker_id),
    broker_id: idParam(s.broker_id),
    broker_account_id: idParam(s.broker_account_id),
    currency: CURRENCIES.find((c) => c === s.currency),
    operation_type: idParam(s.operation_type),
    lifecycle_state: BROKER_LIFECYCLE_STATES.find((c) => c === s.lifecycle_state),
  }),
  component: FindingsInbox,
});

function FindingsInbox() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const qc = useQueryClient();

  const onlyOpen = search.only_open !== "false";
  const sev = search.severity ? (parseInt(search.severity, 10) as Severity) : undefined;
  const runID = search.run_id ? parseInt(search.run_id, 10) : undefined;

  const params = {
    task_type: search.task_type ? [search.task_type] : undefined,
    severity: sev ? [sev] : undefined,
    only_open: onlyOpen,
    run_id: runID,
    limit: 50,
    code: search.code ? [search.code] : undefined,
    root_broker_id: search.root_broker_id ? [search.root_broker_id] : undefined,
    subtree_broker_id: search.subtree_broker_id,
    broker_id: search.broker_id ? [search.broker_id] : undefined,
    broker_account_id: search.broker_account_id ? [search.broker_account_id] : undefined,
    currency: search.currency ? [search.currency] : undefined,
    operation_type: search.operation_type ? [search.operation_type] : undefined,
    lifecycle_state: search.lifecycle_state ? [search.lifecycle_state] : undefined,
  };

  const q = useQuery({
    queryKey: opsKeys.findings(params),
    queryFn: () => fetchFindings(params),
    refetchInterval: 30_000,
  });

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [bulkNotes, setBulkNotes] = useState("");
  const [resolving, setResolving] = useState(false);

  function toggle(id: number) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function doBulkResolve() {
    if (selected.size === 0 || bulkNotes.trim().length < 5) return;
    setResolving(true);
    try {
      await bulkResolveFindings(Array.from(selected), bulkNotes.trim());
      setSelected(new Set());
      setBulkNotes("");
      qc.invalidateQueries({ queryKey: ["recon", "findings"] });
      qc.invalidateQueries({ queryKey: opsKeys.dashboard });
    } finally {
      setResolving(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Select
            value={search.task_type ?? ""}
            onChange={(e) => navigate({ search: (s) => ({ ...s, task_type: (e.target.value as TaskType) || undefined }) })}
            placeholder="Any task type"
            options={TASK_TYPE_OPTIONS}
          />
        </div>
        <div className="w-40">
          <Select
            value={search.severity ?? ""}
            onChange={(e) => navigate({ search: (s) => ({ ...s, severity: e.target.value || undefined }) })}
            placeholder="Any severity"
            options={[
              { value: "1", label: "Critical" },
              { value: "2", label: "Warning" },
              { value: "3", label: "Info" },
            ]}
          />
        </div>
        <div className="w-32">
          <Select
            value={search.currency ?? ""}
            onChange={(e) => navigate({ search: (s) => ({ ...s, currency: e.target.value || undefined }) })}
            placeholder="Any currency"
            options={CURRENCIES.map((c) => ({ value: c, label: c }))}
          />
        </div>
        <div className="w-36">
          <Select
            value={search.lifecycle_state ?? ""}
            onChange={(e) => navigate({ search: (s) => ({ ...s, lifecycle_state: e.target.value || undefined }) })}
            placeholder="Any lifecycle"
            options={BROKER_LIFECYCLE_STATES.map((c) => ({ value: c, label: c }))}
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyOpen}
            onChange={(e) => navigate({ search: (s) => ({ ...s, only_open: e.target.checked ? "true" : "false" }) })}
          />
          Only open
        </label>
        <div className="ml-auto text-xs text-muted">
          {q.data ? `${q.data.Items.length} loaded` : ""}
        </div>
      </div>

      <BrokerScopeFilters
        values={search}
        onApply={(next) => navigate({ search: (s) => ({ ...s, ...next }) })}
      />

      {runID !== undefined && (
        <div className="flex items-center gap-3 rounded border border-border bg-card px-3 py-2 text-sm">
          <span className="text-muted">Scoped to</span>
          <Link
            to="/ops/runs/$id"
            params={{ id: runID }}
            className="font-mono text-xs hover:underline"
          >
            run #{runID}
          </Link>
          <button
            type="button"
            className="ml-auto text-xs text-muted underline hover:text-fg"
            onClick={() =>
              navigate({ search: (s) => ({ ...s, run_id: undefined }) })
            }
          >
            Clear
          </button>
        </div>
      )}

      {selected.size > 0 && (
        <Card>
          <CardBody className="flex items-center gap-3">
            <span className="text-sm">
              {selected.size} selected
            </span>
            <input
              className="flex-1 rounded border border-border bg-bg px-2 py-1 text-sm"
              placeholder="Resolution notes (required, ≥5 chars)"
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
            />
            <Button onClick={doBulkResolve} disabled={resolving || bulkNotes.trim().length < 5}>
              Resolve {selected.size}
            </Button>
            <Button variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardBody className="p-0">
          {q.isLoading && <Skeleton className="m-5 h-32" />}
          {q.data && q.data.Items.length === 0 && (
            <div className="p-8 text-center text-muted">No findings match the current filters.</div>
          )}
          {q.data && q.data.Items.length > 0 && (
            <table className="w-full text-sm">
              <thead className="text-2xs uppercase tracking-wide text-muted">
                <tr className="border-b border-border">
                  <th className="px-2 py-2 w-8"></th>
                  <th className="px-2 py-2 text-left">Severity</th>
                  <th className="px-2 py-2 text-left">Code</th>
                  <th className="px-2 py-2 text-left">Entity</th>
                  <th className="px-2 py-2 text-left">Broker</th>
                  <th className="px-2 py-2 text-left">Currency</th>
                  <th className="px-2 py-2 text-right">Count</th>
                  <th className="px-2 py-2 text-left">Last seen</th>
                  <th className="px-2 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {q.data.Items.map((f) => (
                  <FindingRow key={f.finding_id} f={f} checked={selected.has(f.finding_id)} onToggle={toggle} />
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function FindingRow({ f, checked, onToggle }: { f: Finding; checked: boolean; onToggle: (id: number) => void }) {
  const resolved = !!f.resolved_at_unix_ms && f.resolved_at_unix_ms > 0;
  return (
    <tr className="border-b border-border last:border-0 hover:bg-card">
      <td className="px-2 py-2">
        {!resolved && (
          <input type="checkbox" checked={checked} onChange={() => onToggle(f.finding_id)} />
        )}
      </td>
      <td className="px-2 py-2">
        <Badge color={severityColor(f.severity)}>{severityLabel(f.severity)}</Badge>
      </td>
      <td className="px-2 py-2 font-mono text-xs">
        <Link to="/ops/findings/$id" params={{ id: f.finding_id }} className="hover:underline">
          {f.finding_code}
        </Link>
      </td>
      <td className="px-2 py-2 text-xs">{f.entity_id}</td>
      <td className="px-2 py-2 font-mono text-2xs">{findingBroker(f)}</td>
      <td className="px-2 py-2 text-2xs">{findingCurrency(f)}</td>
      <td className="px-2 py-2 text-right">
        {f.detected_count > 1 ? (
          <Badge color="yellow">×{f.detected_count}</Badge>
        ) : (
          f.detected_count
        )}
      </td>
      <td className="px-2 py-2 text-xs text-muted">
        {fmtRelative(new Date(f.last_detected_at_unix_ms).toISOString())}
      </td>
      <td className="px-2 py-2">
        {resolved ? (
          <Badge color="green">resolved</Badge>
        ) : (
          <Badge color="gray">open</Badge>
        )}
      </td>
    </tr>
  );
}

const DETAIL_BROKER = "broker_id";
const DETAIL_ROOT = "root_broker_id";
const DETAIL_CURRENCY = "currency";

function detailString(f: Finding, key: string): string {
  const v = f.details?.[key];
  return typeof v === "string" ? v : "";
}

function findingBroker(f: Finding): string {
  return detailString(f, DETAIL_BROKER) || detailString(f, DETAIL_ROOT);
}

function findingCurrency(f: Finding): string {
  return detailString(f, DETAIL_CURRENCY);
}

type BrokerScope = Pick<
  Search,
  "code" | "root_broker_id" | "subtree_broker_id" | "broker_id" | "broker_account_id" | "operation_type"
>;

const SCOPE_FIELDS: { key: keyof BrokerScope; label: string }[] = [
  { key: "code", label: "Code" },
  { key: "root_broker_id", label: "Root broker" },
  { key: "subtree_broker_id", label: "Subtree of" },
  { key: "broker_id", label: "Broker" },
  { key: "broker_account_id", label: "Broker account" },
  { key: "operation_type", label: "Operation type" },
];

/**
 * The id-shaped filters (SM-725): typed, then applied together, so the
 * inbox does not refetch on every keystroke.
 */
function BrokerScopeFilters({ values, onApply }: { values: Search; onApply: (next: BrokerScope) => void }) {
  const [draft, setDraft] = useState<BrokerScope>(() => pickScope(values));
  const active = SCOPE_FIELDS.some((f) => values[f.key]);
  return (
    <form
      className="flex flex-wrap items-end gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        const next: BrokerScope = {};
        for (const f of SCOPE_FIELDS) next[f.key] = draft[f.key]?.trim() || undefined;
        onApply(next);
      }}
    >
      {SCOPE_FIELDS.map((f) => (
        <label key={f.key} className="flex flex-col gap-1 text-2xs text-muted">
          {f.label}
          <input
            className="w-40 rounded border border-border bg-bg px-2 py-1 font-mono text-xs text-fg"
            value={draft[f.key] ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
          />
        </label>
      ))}
      <Button type="submit" variant="ghost">
        Apply
      </Button>
      {active ? (
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            const cleared: BrokerScope = {};
            for (const f of SCOPE_FIELDS) cleared[f.key] = undefined;
            setDraft({});
            onApply(cleared);
          }}
        >
          Clear scope
        </Button>
      ) : null}
    </form>
  );
}

function pickScope(v: Search): BrokerScope {
  const out: BrokerScope = {};
  for (const f of SCOPE_FIELDS) out[f.key] = v[f.key];
  return out;
}
